import React, { useCallback, useState } from 'react';

export type ItemCardTabId = 'overview' | 'orders' | 'history' | 'itemData';

export interface ItemCardTab {
  id: ItemCardTabId;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

const TAB_IDS: ItemCardTabId[] = ['overview', 'orders', 'history', 'itemData'];

const readTabFromUrl = (): ItemCardTabId => {
  const value = new URLSearchParams(window.location.search).get('tab');
  return TAB_IDS.includes(value as ItemCardTabId) ? (value as ItemCardTabId) : 'overview';
};

/** Startfane fra ?tab= (dyplenke inn, leses kun ved åpning); deretter holdes valget i minnet. */
export function useItemCardTab(): [ItemCardTabId, (tab: ItemCardTabId) => void] {
  const [tab, setTab] = useState<ItemCardTabId>(readTabFromUrl);
  const select = useCallback((next: ItemCardTabId) => setTab(next), []);
  return [tab, select];
}

interface ItemCardTabBarProps {
  tabs: ItemCardTab[];
  active: ItemCardTabId;
  onSelect: (tab: ItemCardTabId) => void;
}

/** Fanelinje — samme uttrykk som gamle vareinfo-siden (ikon, tekst, telletall). */
export const ItemCardTabBar: React.FC<ItemCardTabBarProps> = ({ tabs, active, onSelect }) => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
    {/* Like brede faner som alltid får plass: på smal skjerm skjules ikonet og teksten kan brytes */}
    <nav className="flex w-full border-b border-gray-200" role="tablist">
      {tabs.map(tab => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab.id)}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-2 sm:px-4 py-3 min-h-[48px] text-xs sm:text-sm font-medium text-center leading-tight border-b-2 -mb-px sm:whitespace-nowrap transition-colors ${
              isActive
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="hidden sm:inline-flex">{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  </div>
);
